import { Alert, AlertTitle, Box, Card, Stack, Tooltip, Typography } from '@mui/material';
import {
  Datagrid,
  Filter,
  FunctionField,
  List,
  TextInput,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import { StPoolContribution } from '../../../services/socialTradingService';
import { Resources } from '../../resource';
import { StIdCell, StStatusChip, stFormatDateTime, stText } from '../StListUi';
import { hasClaimablePoolReward } from './helpers';
import './init';

const ListFilter = (props: any) => {
  const t = useTranslate();
  return (
    <Filter {...(props || {})}>
      <TextInput source="poolId" label={t('module.socialTrading.colPoolId')} alwaysOn resettable />
      <TextInput source="roomId" label={t('module.socialTrading.colRoomId')} alwaysOn resettable />
      <TextInput source="roomName" label={t('module.socialTrading.colRoomName')} alwaysOn resettable />
      <TextInput source="userId" label={t('module.socialTrading.colUserId')} alwaysOn resettable />
    </Filter>
  );
};

export default function SocialTradingPoolContributionsList() {
  const t = useTranslate();

  return (
    <PermissionGuard resource="st-pool-contributions" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.poolContributions')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.poolContributions') },
          ]}
        />
        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>{t('module.socialTrading.poolClaimInfoTitle')}</AlertTitle>
          {t('module.socialTrading.poolClaimInfoBody')}
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('module.socialTrading.poolContributionRowsHint')}
        </Typography>

        <Card sx={{ mt: 2, pb: 2 }}>
          <List
            resource={Resources.socialTradingPoolContributions}
            actions={false}
            empty={false}
            perPage={20}
            exporter={false}
            filters={<ListFilter />}
            sx={{
              '& .RaFilter-form': {
                px: 2,
                pt: 2,
                pb: 1,
                columnGap: 2,
                rowGap: 1.5,
                alignItems: 'flex-end',
              },
            }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                empty={
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {t('module.socialTrading.emptyContributions')}
                  </Typography>
                }
              >
                <FunctionField
                  label={t('module.socialTrading.colPoolId')}
                  render={(row: StPoolContribution) => <StIdCell value={row.poolId} />}
                />
                <FunctionField
                  label={t('module.socialTrading.colRoom')}
                  render={(row: StPoolContribution) => (
                    <Box>
                      <Typography variant="body2">{stText(row.roomName)}</Typography>
                      <StIdCell value={row.roomId} />
                    </Box>
                  )}
                />
                <FunctionField
                  label={t('module.socialTrading.colUserId')}
                  render={(row: StPoolContribution) => <StIdCell value={row.userId} />}
                />
                <FunctionField
                  label={t('module.socialTrading.colNickname')}
                  render={(row: StPoolContribution) => stText(row.nickname)}
                />
                <FunctionField
                  label={t('module.socialTrading.colAccountStatus')}
                  sx={{ minWidth: 140, whiteSpace: 'nowrap' }}
                  render={(row: StPoolContribution) => <StStatusChip status={row.userStatus} />}
                />
                <FunctionField
                  label={t('module.socialTrading.colEnrollmentStatus')}
                  sx={{ minWidth: 140, whiteSpace: 'nowrap' }}
                  render={(row: StPoolContribution) => <StStatusChip status={row.enrollmentStatus} />}
                />
                <FunctionField
                  label={t('module.socialTrading.colVolume')}
                  render={(row: StPoolContribution) => stText(row.volume)}
                />
                <FunctionField
                  label={t('module.socialTrading.colProfit')}
                  render={(row: StPoolContribution) => stText(row.profit)}
                />
                <FunctionField
                  label={t('module.socialTrading.colEstReward')}
                  render={(row: StPoolContribution) => stText(row.estReward)}
                />
                <FunctionField
                  label={t('module.socialTrading.colFinalReward')}
                  render={(row: StPoolContribution) =>
                    hasClaimablePoolReward(row.finalReward) ? (
                      <Tooltip title={t('module.socialTrading.poolClaimOnAppTooltip')}>
                        <Stack spacing={0.25}>
                          <Typography variant="body2">{row.finalReward}</Typography>
                          <Typography variant="caption" color="info.main" noWrap>
                            {t('module.socialTrading.poolClaimOnApp')}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    ) : (
                      stText(row.finalReward)
                    )
                  }
                />
                <FunctionField
                  label={t('module.socialTrading.colCycleStart')}
                  render={(row: StPoolContribution) => stText(stFormatDateTime(row.cycleStart))}
                />
                <FunctionField
                  label={t('module.socialTrading.colCycleEnd')}
                  render={(row: StPoolContribution) => stText(stFormatDateTime(row.cycleEnd))}
                />
                <FunctionField
                  label={t('module.socialTrading.colUpdatedAt')}
                  render={(row: StPoolContribution) => stText(stFormatDateTime(row.updatedAt))}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
}
